import { API_BASE, tokenStorage } from '@/services/apiClient';
import { createFileSink, type FileSink } from './fileSink';

export interface DownloadOkruDeps {
  fetchImpl?: typeof fetch;
  /** Test seam / gesture control — opens the destination sink. */
  openSink?: (filename: string) => Promise<FileSink>;
  getToken?: () => string | null;
}

interface OkruDescriptor { url: string }

/**
 * Stream an ok.ru fight part to disk. Two steps:
 *   1. `GET /:id/okru?part=n` (auth'd) → the HMAC-signed proxy URL. The backend
 *      extracts the progressive ok.ru MP4 over datacenter egress.
 *   2. `GET {API_BASE}{proxyUrl}` (anonymous — the HMAC sig authorizes it) →
 *      stream the bytes to the sink. We deliberately go through the SHARED
 *      segment proxy (not the bespoke `/okru-download` route): the proxy is
 *      Range-capable, concurrency-capped, and CORS-open to our origin
 *      (`access-control-allow-origin` echoes us), so it streams efficiently and
 *      won't saturate the worker on multi-GB fight files.
 *
 * The sink (which may show the File System Access save-picker) is opened FIRST,
 * synchronously within the caller's click gesture, BEFORE any fetch is awaited —
 * otherwise the picker call would be rejected as a non-user-activated gesture.
 * `signal` cancels the in-flight fetch; the caller aborts it to stop a download.
 */
export async function downloadOkruPart(
  titleId: string,
  part: number,
  filename: string,
  signal: AbortSignal,
  deps: DownloadOkruDeps = {},
): Promise<void> {
  const fetchImpl = deps.fetchImpl ?? fetch;
  const getToken = deps.getToken ?? (() => tokenStorage.get());
  const sink = deps.openSink ? await deps.openSink(filename) : await createFileSink(filename, 'video/mp4');
  try {
    const token = getToken();
    // 1) resolve the signed proxy URL (auth'd; datacenter extract + short-TTL session)
    const descUrl = `${API_BASE}/api/sports-vod-stream/${encodeURIComponent(titleId)}/okru?part=${encodeURIComponent(part)}`;
    const descRes = await fetchImpl(descUrl, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
      signal,
    });
    if (!descRes.ok) throw new Error(`okru resolve failed: ${descRes.status}`);
    const desc = (await descRes.json()) as OkruDescriptor;
    if (!desc?.url) throw new Error('okru: no proxy url');

    // 2) stream the MP4 bytes from the signed proxy (anonymous, CORS-open, Range-capable)
    const res = await fetchImpl(`${API_BASE}${desc.url}`, { signal });
    if (!res.ok || !res.body) throw new Error(`okru download failed: ${res.status}`);
    const reader = res.body.getReader();
    try {
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        if (value) await sink.write(value);
      }
    } finally {
      try { reader.releaseLock(); } catch { /* already released/errored */ }
    }
    await sink.close();
  } catch (err) {
    await sink.abort().catch(() => { /* best-effort */ });
    throw err;
  }
}
